// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Dobrev IT Ltd

// CryptoEngine drives the Go-WASM OpenPGP engine in a Web Worker. The worker
// (public/wasm_bridge.js) is a classic worker loaded by URL — kept out of the
// Vite bundle so its importScripts(wasm_exec.js) works unchanged.
//
// Loading is best-effort: if the WASM engine is unavailable the rest of the app
// still works (messages are sent without device encryption, surfaced in the UI).

import type { SigchainLink, SigchainValidation } from "./types";

export interface EncryptedMessage {
  algorithm: string;
  sender_device: string;
  ciphertext: string;
  session_keys: Record<string, string>;
  session_id: string;
  signature?: string;
}

interface Pending {
  resolve: (v: unknown) => void;
  reject: (e: Error) => void;
}

export class CryptoEngine {
  private worker?: Worker;
  private seq = 0;
  private pending = new Map<number, Pending>();
  ready = false;

  // init boots the worker + WASM runtime. Resolves false (not throws) if the
  // engine can't be loaded, so callers can degrade gracefully.
  async init(): Promise<boolean> {
    try {
      const worker = new Worker("/wasm_bridge.js");
      worker.onmessage = (e: MessageEvent) => {
        const { id, ok, result, error } = e.data || {};
        if (id == null) {
          // A worker-level failure (the Go runtime panicked → self.onerror posts
          // {id:null}). It isn't tied to one call, so fail EVERY in-flight call —
          // otherwise their pending entries leak and their promises hang forever.
          const err = new Error(error || "crypto worker error");
          for (const [, p] of this.pending) p.reject(err);
          this.pending.clear();
          return;
        }
        const p = this.pending.get(id);
        if (!p) return;
        this.pending.delete(id);
        ok ? p.resolve(result) : p.reject(new Error(error));
      };
      this.worker = worker;
      await this.call("init", ["main.wasm"]);
      this.ready = true;
      return true;
    } catch {
      this.ready = false;
      return false;
    }
  }

  // dispose terminates the worker and fails any in-flight calls. Not wired into
  // teardown today (the engine is a per-session singleton); available for a future
  // per-login re-creation so it can't strand the old worker or its pending calls.
  dispose(): void {
    this.worker?.terminate();
    this.worker = undefined;
    this.ready = false;
    for (const [, p] of this.pending) p.reject(new Error("crypto engine disposed"));
    this.pending.clear();
  }

  private call<T>(op: string, args: unknown[] = []): Promise<T> {
    if (!this.worker) return Promise.reject(new Error("crypto worker not started"));
    const id = ++this.seq;
    return new Promise<T>((resolve, reject) => {
      // Time out a call whose reply never arrives (a dead worker, a lost message)
      // so it can't hang or leak its pending entry. Cleared on settle.
      const timer = setTimeout(() => {
        if (this.pending.delete(id)) reject(new Error(`crypto op '${op}' timed out`));
      }, 15_000);
      // The Go engine signals failure by returning {error: msg} rather than
      // throwing, so surface that as a rejection here.
      const wrapped = (v: unknown) => {
        clearTimeout(timer);
        if (v && typeof v === "object" && "error" in v && typeof (v as { error: unknown }).error === "string") {
          reject(new Error((v as { error: string }).error));
        } else {
          resolve(v as T);
        }
      };
      this.pending.set(id, { resolve: wrapped, reject: (e) => { clearTimeout(timer); reject(e); } });
      this.worker!.postMessage({ id, op, args });
    });
  }

  // initEngine loads this device's private key into the engine.
  initEngine(deviceId: string, armoredPrivateKey: string, passphrase = ""): Promise<boolean> {
    return this.call("initEngine", [deviceId, armoredPrivateKey, passphrase]);
  }

  // addDecryptionKey registers a prior device's key so the engine can read
  // messages that were encrypted to that earlier device (same-browser history).
  addDecryptionKey(armoredPrivateKey: string, passphrase = ""): Promise<boolean> {
    return this.call("addDecryptionKey", [armoredPrivateKey, passphrase]);
  }

  // encryptForDevices encrypts plaintext for {deviceId: armoredPublicKey}.
  encryptForDevices(plaintext: string, recipients: Record<string, string>): Promise<EncryptedMessage> {
    return this.call("encryptPayloadForDevices", [plaintext, recipients]);
  }

  decrypt(msg: EncryptedMessage): Promise<string> {
    return this.call("decryptPayload", [msg]);
  }

  // generateDeviceKey mints a fresh OpenPGP device identity in the worker.
  generateDeviceKey(
    name: string,
    email: string,
  ): Promise<{ armored_private_key: string; armored_public_key: string; fingerprint: string }> {
    return this.call("generateDeviceKey", [name, email]);
  }

  // ---- device sigchain / identity proofs ----------------------------------
  signSigchainLink(link: SigchainLink, armoredPrivateKey: string, passphrase = ""): Promise<{ signature: string }> {
    return this.call("signSigchainLink", [link, armoredPrivateKey, passphrase]);
  }
  nextPrevHash(link: SigchainLink): Promise<{ prev_hash: string }> {
    return this.call("nextPrevHash", [link]);
  }
  validateSigchain(userID: string, anchorPublicKey: string, links: SigchainLink[]): Promise<SigchainValidation> {
    return this.call("validateSigchain", [userID, anchorPublicKey, links]);
  }

  // ---- encrypted key backup -----------------------------------------------
  // generateRecoveryKey mints a strong, printable recovery key (the default
  // recovery secret).
  generateRecoveryKey(): Promise<{ recovery_key: string }> {
    return this.call("generateRecoveryKey", []);
  }
  // backupKeyring encrypts a keyring JSON under the recovery secret, returning the
  // opaque blob to store server-side.
  backupKeyring(recoverySecret: string, keyringJson: string): Promise<{ blob: string }> {
    return this.call("backupKeyring", [recoverySecret, keyringJson]);
  }
  // restoreKeyring decrypts a backup blob with the recovery secret.
  restoreKeyring(recoverySecret: string, blob: string): Promise<{ keyring: string }> {
    return this.call("restoreKeyring", [recoverySecret, blob]);
  }

  // ---- key transparency ----------------------------------------------------
  // auditKeyTransparency verifies a user's key is committed in the server's signed
  // transparency log (the input is JSON: user_id, anchor, links, str, server_key,
  // audit_path, index).
  auditKeyTransparency(inputJson: string): Promise<{ verified: boolean; error?: string }> {
    return this.call("auditKeyTransparency", [inputJson]);
  }

  // verifySTRSignature checks a gossiped signed tree root's signature against the
  // server key (the input is JSON: {str, server_key}) and returns its canonical chain
  // hash — the integrity gate before trusting a peer's STR as equivocation evidence.
  verifySTRSignature(inputJson: string): Promise<{ verified: boolean; hash?: string; error?: string }> {
    return this.call("verifySTRSignature", [inputJson]);
  }
}
